import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <main className="bg-ink min-h-screen" data-testid="blog-page">
      <Seo title="Dubai Property Journal — Haven & Hill Properties"
           description="Market insight, buying guides and investment analysis from Dubai real-estate advisors with 20 years of leadership."
           path="/blog" />
      <Navbar />
      <div className="pt-32 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.35em] text-gold-light/90 font-medium mb-5">The journal</p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F8F6F0] leading-tight max-w-2xl">
          Market insight, <span className="italic gold-text">in plain language.</span>
        </h1>

        {loading ? (
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => <div key={i} className="rounded-2xl bg-ink-card/60 animate-pulse aspect-[4/5]" />)}
          </div>
        ) : (
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {posts.map((p, i) => (
              <motion.article
                key={p.slug}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.06, 0.4), duration: 0.55 }}
                className={`gold-ring rounded-2xl overflow-hidden bg-ink-card group flex flex-col ${i === 0 ? "md:col-span-2" : ""}`}
                data-testid={`blog-card-${p.slug}`}
              >
                <div className={`relative overflow-hidden ${i === 0 ? "aspect-[21/9]" : "aspect-[16/9]"}`}>
                  <img src={p.image_url} alt={p.title} loading="lazy"
                       className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-transparent" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">{p.date}</p>
                  <h2 className="mt-2 font-serif text-xl text-[#F8F6F0] leading-snug">{p.title}</h2>
                  <p className="mt-2.5 text-sm text-zinc-400 leading-relaxed flex-1">{p.excerpt}</p>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-read-${p.slug}`}
                        className="mt-5 inline-flex items-center gap-2 text-xs font-semibold tracking-wide text-gold-light hover:text-gold-pale transition-colors self-start">
                    Read article <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
